import {useTraduire} from '../i18n/langue';
import {T} from '../i18n/textes';
import {useState} from 'react';
import {
    ALL_TAGS,
    ALL_TEAMS,
    FeedbackStatus,
    FeedbackTag,
    FeedbackTeam,
    STATUS_LABELS,
    TAG_LABELS,
    TEAM_LABELS,
} from '../types/feedback';
import {Broom} from '@phosphor-icons/react';

interface BulkActionsBarProps {
    selectedCount: number;
    onApply: (updates: {
        status?: FeedbackStatus;
        team?: FeedbackTeam;
        addTags?: FeedbackTag[];
    }) => void;
    onCancel: () => void;
    loading: boolean;
}

export function BulkActionsBar({selectedCount, onApply, onCancel, loading}: BulkActionsBarProps) {
    const t = useTraduire();
    const [status, setStatus] = useState<FeedbackStatus | ''>('');
    const [team, setTeam] = useState<FeedbackTeam | ''>('');
    const [selectedTags, setSelectedTags] = useState<FeedbackTag[]>([]);

    const handleApply = () => {
        const updates: {
            status?: FeedbackStatus;
            team?: FeedbackTeam;
            addTags?: FeedbackTag[];
        } = {};

        if (status) updates.status = status;
        if (team) updates.team = team;
        if (selectedTags.length > 0) updates.addTags = selectedTags;

        if (Object.keys(updates).length > 0) {
            onApply(updates);
        }
    };

    const toggleTag = (tag: FeedbackTag) => {
        setSelectedTags((prev) =>
            prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
        );
    };

    const hasChanges = status || team || selectedTags.length > 0;

    return (
        <div className="bg-primary-lighter/30 border border-primary-lighter rounded-xl p-4 mb-4">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <span
                        className="inline-flex items-center justify-center w-6 h-6 bg-primary text-white text-sm font-bold rounded-full">
                        {selectedCount}
                    </span>
                    <span className="text-[11px] font-medium text-primary-dark uppercase tracking-wider">
                        feedback{selectedCount > 1 ? 's' : ''} selected
                    </span>
                </div>
                <button
                    onClick={onCancel}
                    className="text-[11px] text-primary hover:text-primary-dark transition-colors flex items-center gap-1.5 uppercase tracking-wider font-medium"
                >
                    <Broom size={18} weight="regular"/>
                    Clear selection
                </button>
            </div>

            <div className="flex flex-wrap gap-4 items-end">
                {/* Status */}
                <div className="w-36">
                    <label className="block text-[11px] font-medium text-primary-dark mb-1 uppercase tracking-wider">
                        Set Status
                    </label>
                    <select
                        value={status}
                        onChange={(e) => setStatus(e.target.value as FeedbackStatus | '')}
                        className="w-full px-2 py-1.5 text-sm border border-primary-lighter focus:outline-none focus:ring-primary focus:border-primary"
                    >
                        <option value="">— No change —</option>
                        {Object.entries(STATUS_LABELS).map(([value, libelle]) => (
                            <option key={value} value={value}>
                                {t(libelle)}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Team */}
                <div className="w-36">
                    <label className="block text-[11px] font-medium text-primary-dark mb-1 uppercase tracking-wider">
                        Set Team
                    </label>
                    <select
                        value={team}
                        onChange={(e) => setTeam(e.target.value as FeedbackTeam | '')}
                        className="w-full px-2 py-1.5 text-sm border border-primary-lighter focus:outline-none focus:ring-primary focus:border-primary"
                    >
                        <option value="">— No change —</option>
                        {ALL_TEAMS.map((equipe) => (
                            <option key={equipe} value={equipe}>
                                {t(TEAM_LABELS[equipe])}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Tags */}
                <div className="flex-1 min-w-[200px]">
                    <label className="block text-[11px] font-medium text-primary-dark mb-1 uppercase tracking-wider">
                        Add Tags
                    </label>
                    <div className="flex flex-wrap gap-1">
                        {ALL_TAGS.map((tag) => (
                            <button
                                key={tag}
                                onClick={() => toggleTag(tag)}
                                className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                                    selectedTags.includes(tag)
                                        ? 'bg-primary text-white border-primary'
                                        : 'bg-white text-primary-dark border-primary-lighter hover:border-primary'
                                }`}
                            >
                                {t(TAG_LABELS[tag])}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Apply button */}
                <button
                    onClick={handleApply}
                    disabled={!hasChanges || loading}
                    className="px-4 py-2 bg-primary text-white text-sm font-medium rounded-pill hover:bg-primary-light focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed shadow-bo transition-colors"
                >
                    {loading ? t(T.application) : t(T.appliquer)}
                </button>
            </div>
        </div>
    );
}
